import {Injectable, Renderer2, RendererFactory2} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {ThemeModeEnum} from '../../typings/enums/theme-mode.enum';
import {LOCAL_STORAGE_KEY_CONSTANTS} from '../../core/constants/local-key-storage.constants';
import {LocalStorageProviderService} from '../provider-services/local-storage.provider-service';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private renderer: Renderer2;
  private themeSubject = new BehaviorSubject<ThemeModeEnum>(ThemeModeEnum.dark);
  public theme$: Observable<ThemeModeEnum> = this.themeSubject.asObservable();

  constructor(rendererFactory: RendererFactory2, public localStorageProviderService: LocalStorageProviderService) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.loadInitialTheme();
  }

  public loadInitialTheme(): void {
    // Check localStorage first
    const savedTheme = this.localStorageProviderService.getItem(LOCAL_STORAGE_KEY_CONSTANTS.THEME) as ThemeModeEnum | null;

    // Check system preference if no saved theme
    if (!savedTheme) {
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const initialTheme = systemPrefersDark ? ThemeModeEnum.dark : ThemeModeEnum.light;
      this.setTheme(initialTheme);
    } else {
      this.setTheme(savedTheme);
    }
  }

  setTheme(theme: ThemeModeEnum): void {
    // Remove previous theme attribute
    this.renderer.removeAttribute(document.documentElement, 'data-theme');

    // Set new theme attribute
    if (theme == ThemeModeEnum.dark) {
      this.renderer.setAttribute(document.documentElement, 'data-theme', 'dark');
    }

    // Save to localStorage
    this.localStorageProviderService.setItem(LOCAL_STORAGE_KEY_CONSTANTS.THEME, theme);

    // Update subject
    this.themeSubject.next(theme);
  }

  toggleTheme(): void {
    const currentTheme = this.themeSubject.value;
    const newTheme = currentTheme === ThemeModeEnum.light ? ThemeModeEnum.dark : ThemeModeEnum.light;
    this.setTheme(newTheme);
  }

  getCurrentTheme(): ThemeModeEnum {
    return this.themeSubject.value;
  }

  isDarkMode(): boolean {
    return this.themeSubject.value === ThemeModeEnum.dark;
  }

  isLightMode(): boolean {
    return this.themeSubject.value === ThemeModeEnum.light;
  }
}

